import { createSlice } from '@reduxjs/toolkit';
import { ThunkStatus } from '../../enums/ThunkStatus';
import { runAcceptHandler, runCancelHandler } from './alertDialog.thunk';
import { alertDialogSliceName } from './alertDialog.selectors';

/**
 * State that maintains the common alert dialog texts, settings and handlers.
 *
 * All these values are set by calling the hook open method and cleared
 * when the dialog is canceled or accepted.
 *
 * The initial state uses practical defaults, so that they should be replaced
 * only when needed with the call to hook method 'onOpenAlertDialog'
 */
const initialState = {
  isOpen: false,
  texts: {
    title: undefined,
    message: undefined,
    confirmButton: undefined,
    cancelButton: undefined,
  },
  settings: {
    showCancelButton: false,
    showCloseButton: false,
  },
  loading: false,
  acceptHandler: {
    func: () => {},
    args: {},
  },
  cancelHandler: {
    func: () => {},
    args: {},
  },
};

/**
 * The slice contains reducers to open the dialog with updated properties and to close it.
 * Also, one thunk reducer that runs the confirmation handler.
 */
export const alertDialogSlice = createSlice({
  name: alertDialogSliceName,
  initialState,
  reducers: {
    openDialog(state, action) {
      state.isOpen = true;
      state.texts = {
        ...state.texts,
        ...action.payload.texts,
      };
      state.settings = {
        ...state.settings,
        ...action.payload.settings,
      };
      state.acceptHandler = {
        ...state.acceptHandler,
        ...action.payload.acceptHandler,
      };
      state.cancelHandler = {
        ...state.cancelHandler,
        ...action.payload.cancelHandler,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(runAcceptHandler.pending, (state) => {
        state.status = ThunkStatus.LOADING;
        state.loading = true;
      })
      .addCase(runAcceptHandler.fulfilled, (state) => {
        state.status = ThunkStatus.SUCCEEDED;
        resetState(state);
      })
      .addCase(runCancelHandler.pending, (state) => {
        state.status = ThunkStatus.LOADING;
        state.isOpen = false;
        state.loading = true;
      })
      .addCase(runCancelHandler.fulfilled, (state) => {
        state.status = ThunkStatus.SUCCEEDED;
        resetState(state);
      });
    // error handling?
  },
});

const resetState = (state) => {
  state.isOpen = false;
  state.texts = initialState.texts;
  state.settings = initialState.settings;
  state.loading = false;
  state.acceptHandler = initialState.acceptHandler;
  state.cancelHandler = initialState.cancelHandler;
};

/* Actions */
export const { openDialog, closeDialog } = alertDialogSlice.actions;
